import { lastNameSortKey } from "../../../utility/bowlerName";

export const SCORING_SORT_FIELDS = [
  { id: "lane", label: "Lane", defaultOrder: "asc" },
  { id: "name", label: "Name", defaultOrder: "asc" },
  { id: "score", label: "Score", defaultOrder: "desc" },
  { id: "added", label: "Added", defaultOrder: "asc" },
];

export const defaultSortOrderFor = (sortBy) =>
  SCORING_SORT_FIELDS.find((field) => field.id === sortBy)?.defaultOrder ||
  "asc";

const squadSortKey = (team) =>
  `${team?.squad?.squadDate || ""}\t${team?.squad?.squadTime || ""}`;

export const laneSortValue = (team) => {
  const lane = Number(team?.startingLane);
  if (Number.isInteger(lane) && lane > 0) return lane;
  const pair = Number(team?.startingPair);
  if (Number.isInteger(pair) && pair > 0) return pair;
  return null;
};

const slotSortValue = (team) => {
  const slot = Number(team?.slot);
  return Number.isInteger(slot) && slot > 0 ? slot : 0;
};

export const nameSortKey = (team) => {
  const teamName = String(team?.teamName || "").trim();
  if (teamName) return teamName.toLowerCase();
  const bowlerName =
    team?.bowlers?.[0]?.name || team?.bowlers?.[0]?.bowlerName || "";
  return lastNameSortKey(bowlerName);
};

export const teamScoreSortValue = (team, scoreForTeam) => {
  if (typeof scoreForTeam === "function") {
    const live = scoreForTeam(team);
    if (live != null && Number.isFinite(Number(live))) return Number(live);
  }
  const handicap = Number(team?.teamTotals?.handicapTotal);
  if (Number.isFinite(handicap) && handicap > 0) return handicap;
  const scratch = Number(team?.teamTotals?.scratchTotal);
  if (Number.isFinite(scratch) && scratch > 0) return scratch;
  if (handicap === 0 || scratch === 0) return 0;
  return null;
};

const compareNullableNumber = (left, right, direction) => {
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  if (left === right) return 0;
  return (left - right) * direction;
};

const compareText = (left, right, direction) => {
  const compared = String(left).localeCompare(String(right), undefined, {
    sensitivity: "base",
  });
  return compared * direction;
};

export const compareScoringTeams = (
  left,
  right,
  sortBy = "lane",
  sortOrder = "asc",
  { scoreForTeam, addedIndex } = {}
) => {
  const direction = sortOrder === "desc" ? -1 : 1;
  const added = (team) => {
    if (team?.created) {
      const time = Date.parse(team.created);
      if (Number.isFinite(time)) return time;
    }
    return addedIndex?.(team) ?? 0;
  };

  let compared = 0;
  if (sortBy === "name") {
    compared = compareText(nameSortKey(left), nameSortKey(right), direction);
  } else if (sortBy === "score") {
    compared = compareNullableNumber(
      teamScoreSortValue(left, scoreForTeam),
      teamScoreSortValue(right, scoreForTeam),
      direction
    );
  } else if (sortBy === "added") {
    compared = compareNullableNumber(added(left), added(right), direction);
  } else {
    compared = compareText(squadSortKey(left), squadSortKey(right), 1);
    if (compared === 0) {
      compared = compareNullableNumber(
        laneSortValue(left),
        laneSortValue(right),
        direction
      );
    }
    if (compared === 0) {
      compared = compareNullableNumber(
        slotSortValue(left),
        slotSortValue(right),
        direction
      );
    }
  }

  if (compared !== 0) return compared;
  return compareText(left?.id || "", right?.id || "", 1);
};

export const sortScoringTeams = (
  teams = [],
  sortBy = "lane",
  sortOrder = "asc",
  options = {}
) => {
  const addedIndex = options.addedIndex;
  const indexById = addedIndex
    ? null
    : new Map((teams || []).map((team, index) => [team.id, index]));
  const resolveIndex = (team) =>
    addedIndex ? addedIndex(team) : indexById.get(team.id) ?? 0;
  return [...(teams || [])].sort((left, right) =>
    compareScoringTeams(left, right, sortBy, sortOrder, {
      ...options,
      addedIndex: resolveIndex,
    })
  );
};
